import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import {
    Plus,
    Edit2,
    Trash2,
    Eye,
    EyeOff,
    Search,
    FileText
} from "lucide-react";
import toast from "react-hot-toast";

export default function ArticleList() {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [langFilter, setLangFilter] = useState("en");

    const fetchArticles = async () => {
        try {
            const res = await api.get(`/articles?q=${search}&lang=${langFilter}&adminFilter=true`);
            setArticles(res.data);
        } catch (err) {
            toast.error("The Library is sealed.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(fetchArticles, 300);
        return () => clearTimeout(timer);
    }, [search, langFilter]);

    const handleDelete = async (id) => {
        if (!window.confirm("Burn this scroll? This action cannot be undone.")) return;
        try {
            await api.delete(`/admin/articles/${id}`);
            toast.success("Scroll burned.");
            fetchArticles();
        } catch (err) {
            toast.error("Failed to delete");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-light text-white">
                        The <span className="font-bold text-cyan-400">Printing Press</span>
                    </h2>
                    <p className="text-gray-500 mt-2">Manage the wisdom of the network.</p>
                </div>
                <button
                    onClick={() => navigate("/admin/articles/new")}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-bold transition-colors"
                >
                    <Plus size={18} />
                    Write New Scroll
                </button>
            </div>

            {/* Filters */}
            <div className="flex gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input
                        type="text"
                        placeholder="Search wisdom..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-cyan-500/50 transition-colors"
                    />
                </div>
                <select
                    value={langFilter}
                    onChange={(e) => setLangFilter(e.target.value)}
                    className="bg-black/40 border border-white/10 rounded-xl px-4 text-sm text-white focus:outline-none"
                >
                    <option value="en">English View</option>
                    <option value="hi">Hinglish View</option>
                </select>
            </div>

            {/* Table */}
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Inking quills...</div>
                ) : articles.length === 0 ? (
                    <div className="p-12 flex flex-col items-center justify-center text-gray-500">
                        <FileText size={48} className="text-cyan-500 mb-4 opacity-50" />
                        <p className="text-lg">The Library is Empty.</p>
                        <p className="text-sm opacity-60">Write the first scroll.</p>
                    </div>
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-black/40 text-[10px] uppercase tracking-wider text-gray-500">
                                <th className="p-4 font-medium">Scroll</th>
                                <th className="p-4 font-medium">Domain</th>
                                <th className="p-4 font-medium text-center">Locales</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5 text-sm">
                            {articles.map((article) => (
                                <tr key={article.id} className="hover:bg-white/5 transition-colors group">
                                    <td className="p-4">
                                        <div className="font-bold text-white leading-tight">{article.title || "Untitled Scroll"}</div>
                                        <div className="text-gray-500 text-[10px] mt-1 uppercase tracking-tighter">By {article.author?.name}</div>
                                    </td>
                                    <td className="p-4">
                                        <span className="bg-white/5 border border-white/10 text-gray-400 px-2 py-1 rounded text-[10px]">
                                            {article.category?.label || "Wisdom"}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex justify-center gap-1">
                                            {["en", "hi"].map(l => (
                                                <span 
                                                    key={l}
                                                    className={`w-6 h-6 flex items-center justify-center rounded text-[8px] font-black ${
                                                        article.availableLocales?.includes(l)
                                                        ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                                                        : "bg-white/5 text-gray-700 border border-transparent"
                                                    }`}
                                                >
                                                    {l.toUpperCase()}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            {article.status === 'PUBLISHED' ? (
                                                <span className="flex items-center gap-1 text-green-500 text-[10px] font-bold p-1 bg-green-500/10 rounded">
                                                    <Eye size={10} /> PUBLIC
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1 text-amber-500 text-[10px] font-bold p-1 bg-amber-500/10 rounded">
                                                    <EyeOff size={10} /> DRAFT
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 text-right space-x-1">
                                        <button
                                            onClick={() => navigate(`/admin/articles/${article.id}`)}
                                            className="p-2 hover:bg-white/10 text-gray-400 hover:text-white rounded-lg transition-all"
                                            title="Edit"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(article.id)}
                                            className="p-2 hover:bg-red-500/10 text-gray-500 hover:text-red-400 rounded-lg transition-all"
                                            title="Burn"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
